
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  EventAvailable,
  Refresh,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Homepage/Navbar/Navbar";
import Footer from "../components/Homepage/Footer/Footer";

const API_URL = "https://rukap.edu.in/leave-api";
// const API_URL = "http://127.0.0.1:5000";

const MyApplications = () => {
  const navigate = useNavigate();

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const token = sessionStorage.getItem("leaveToken");

  const loadApplications = async () => {
    if (!token) {
      navigate("/leave-portal", { replace: true });
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.get(
        `${API_URL}/api/leaves/my-applications`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setApplications(response.data.applications || []);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("leaveToken");
        sessionStorage.removeItem("leaveUser");
        navigate("/leave-portal", { replace: true });
        return;
      }

      setError(
        err.response?.data?.message ||
          "Unable to load your leave applications."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApplications();
    // Load when the page is mounted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (value) => {
    if (!value) return "-";

    const dateString =
      typeof value === "string"
        ? value.slice(0, 10)
        : new Date(value).toISOString().slice(0, 10);

    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const formatDateTime = (value) => {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "APPROVED":
        return "success";
      case "REJECTED":
        return "error";
      case "PENDING":
      case "SUBMITTED":
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <>
      <Navbar />

      <Box
        sx={{
          minHeight: "85vh",
          py: 4,
          bgcolor: "#f4f7fb",
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                color="#0B3C5D"
              >
                My Leave Applications
              </Typography>

              <Typography color="text.secondary" sx={{ mt: 1 }}>
                View your submitted leave requests and their status.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadApplications}
                disabled={loading}
              >
                Refresh
              </Button>

              <Button
                variant="contained"
                startIcon={<ArrowBack />}
                onClick={() =>
                  navigate("/leave-portal/employee-dashboard")
                }
                sx={{ bgcolor: "#0B3C5D" }}
              >
                Dashboard
              </Button>
            </Stack>
          </Stack>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>
                Loading your applications...
              </Typography>
            </Box>
          ) : applications.length === 0 ? (
            <Card elevation={1} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ py: 7, textAlign: "center" }}>
                <EventAvailable
                  sx={{ fontSize: 55, color: "#0B3C5D", mb: 1 }}
                />

                <Typography variant="h6" fontWeight={600}>
                  No Leave Applications Found
                </Typography>

                <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
                  You have not submitted any leave applications yet.
                </Typography>

                <Button
                  variant="contained"
                  onClick={() => navigate("/leave-portal/apply")}
                  sx={{ bgcolor: "#0B3C5D" }}
                >
                  Apply for Leave
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Stack spacing={2}>
              {applications.map((application) => (
                <Card
                  key={application.leave_id}
                  elevation={1}
                  sx={{ borderRadius: 3 }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      spacing={1}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          color="#0B3C5D"
                        >
                          {application.application_no}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          Submitted:{" "}
                          {formatDateTime(application.submitted_at)}
                        </Typography>
                      </Box>

                      <Chip
                        label={application.status}
                        color={getStatusColor(application.status)}
                        variant="outlined"
                      />
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={3}
                    >
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Leave Type
                        </Typography>
                        <Typography fontWeight={600}>
                          {application.leave_type}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Total Days
                        </Typography>
                        <Typography fontWeight={600}>
                          {application.total_days}
                        </Typography>
                      </Box>
                    </Stack>

                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Selected Leave Dates
                      </Typography>

                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        gap={1}
                        sx={{ mt: 1 }}
                      >
                        {(application.dates || []).map((date) => (
                          <Chip
                            key={date}
                            icon={<EventAvailable />}
                            label={formatDate(date)}
                            variant="outlined"
                            color="primary"
                          />
                        ))}
                      </Stack>
                    </Box>

                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Purpose
                      </Typography>
                      <Typography sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}>
                        {application.purpose}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default MyApplications;
